import React, { useMemo, useRef, useState } from 'react';
import CardCommon from '@op/components/CardCommon';
import useLanguage from '@op/hooks/useLanguage';
import { Button, Form, Modal, Radio } from 'tea-component';
import GPT from '@op/components/GPT';
import BubbleCommon, {
  aigcAdvancedSummaryTips,
  aigcGptModalTypeTips,
  TipsMapType,
} from '@op/components/BubbleCommon';
import { PromptConfirmItemType } from '@op/components/GPT/types';
import { getDefaultPromptMap, getPromptText } from '@op/components/GPT/utils';
import PromptSection from './PromptSection';
import PromptEditModal, {
  PromptEditDraft,
} from '../PromptEditModal';
import { useConfirm } from '@op/components/GPT/hooks/useConfirm';
import { useReportPermissionNotify } from '@op/components/ReportPermission';
import RepeatSetting from '../RepeatSetting';
import './index.scss';
import { FilterFixed, useFilterDataHook } from '@op/components/FilterCommon';
import { useSaveDetailForm } from '@op/components/GPT/hooks/useSaveForm';
import { useAiSaveName } from '@op/pages/AIAnalysisHub/hooks';
import { useGetSingePageSourceRule } from '@op/hooks/useGetPageSourceRule';
import AiImg from '@op/assets/svg/icon-ai.svg';
import { getReportSuffix } from '@op/utils/time';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import usePromptCollect from '@op/pages/AIAnalysisHub/CustomAISummary/hooks/usePromptCollect';
import useCustomDefaultPrompt from '@op/pages/AIAnalysisHub/CustomAISummary/hooks/useCustomDefaultPrompt';
import SvgIcon from '@home/components/SvgIcon';

export default function AISummarySetting(props: any) {
  const {
    gameInfo,
    wholeGameInfo,
    handleConfirm,
    defaultActive = 1,
    userInfo,
    backCondOriginData,
    changeTab,
    children,
  } = props;

  const {
    scrollBody,
    loading,
    filter,
    changeFilter,
    filterOptions,
    renderExcludeFilterMemo,
    filterCommonParams,
    confirmRef,
    filterConfirm,
    filterReset,
    filterSave,
  } = useFilterDataHook({
    gameInfo,
    pageName: 'Custom_AI_Summary',
    renderExcludeFilter: ['competitorGames'],
  });

  const { formatMessage } = useLanguage();
  const { promptList, defaultPromptList } = useCustomDefaultPrompt({
    gameInfo: wholeGameInfo,
  });
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('');
  const [promptValue, setPromptValue] = useState(undefined);
  const [activePromptId, setActivePromptId] = useState('');
  // 弹窗内编辑 Prompt 设置的开关
  const [promptEditVisible, setPromptEditVisible] = useState(false);
  const isGenerate = useRef(false);
  const oriFilter = useRef(null);
  const promptEdit = useRef(false);

  // 权限控制
  const { Generate_Repeat_Report } = useGetSingePageSourceRule({
    keys: ['Generate_Repeat_Report'],
  });

  const {
    resultType,
    emailList,
    aiSaveTitle,
    setAiSaveTitle,
    isSave,
    saveEmailList,
    remainder,
    total,
    setRemainder,
    advancedLimitLoading,
    gptOptions,
    setGptOptions,
    setErrorText,
    prompt,
    setPrompt,
    confirmDisabled,
    gptType,
    realPromptList,
    errorText,
  } = useSaveDetailForm({
    userInfo,
    getDefaultSaveName,
    defaultIsSave: true,
    promptList,
    promptValue,
    loading,
  });

  // useEffect(() => {
  //   const currentPrompt = getDefaultPromptText(
  //     realPromptList,
  //     defaultActive,
  //     gptType,
  //   );
  //   if (currentPrompt && resultType === 'page') {
  //     setPrompt(currentPrompt);
  //   }
  // }, [formatMessage]);

  const togglePromptModal = (modalType = 'history') => {
    setModalType(modalType);
    setModalVisible(!modalVisible);
  };

  function getDefaultSaveName(timeTitle?: string) {
    return `Custom AI Summary - ${timeTitle} ${getReportSuffix()}`;
  }

  const { filterOnChange } = useAiSaveName({
    getSaveName: getDefaultSaveName,
    setSaveName: setAiSaveTitle,
  });

  const {
    loading: historyLoading,
    userPromptList,
    savedPromptList,
    setUserHistoryPrompt,
    getUserHistoryPrompt,
    savePrompt,
    updatePrompt,
    deletePrompt,
    applyPrompt,
  } = usePromptCollect({
    gameInfo: wholeGameInfo,
    promptList: realPromptList,
  });

  const {
    advancedConfirm,
    loading: confirmLoading,
    downloadAdvanceConfirm,
  } = useConfirm({
    filter,
    gameInfo,
    setRemainder,
  });

  const permissionNotify = useReportPermissionNotify();

  const handleGptConfirm = async (filterValue: any = filter, oriValue: any) => {
    const promptInfo: PromptConfirmItemType = {
      prompt,
      resultType,
      gptOptions,
      modelType: gptType,
    };

    setUserHistoryPrompt(prompt);

    if (isSave) {
      promptInfo.saveName = aiSaveTitle;

      if (emailList.length > 0) {
        promptInfo.emailList = emailList;
        saveEmailList();
      }
    }

    try {
      const createResponse = await advancedConfirm(
        { ...promptInfo, resultType: 'page' },
        filterValue,
      );
      const { task_id: taskId } = createResponse || {};

      if (!taskId) {
        handleConfirm?.({}, null, {});
        return;
      }

      if (resultType === 'download') {
        downloadAdvanceConfirm(taskId, promptInfo);
        // TODO: 这里看产品情况，是直接在当前页面进行展示，还是将下面展示为空
        handleConfirm?.({}, null, { isSave });
      } else if (taskId) {
        // 暂存权限目标，交由 useReportCreateSuccess 在滚动弹通知时打开权限面板
        if (isSave) {
          permissionNotify?.setPendingReportTarget(createResponse);
        }
        handleConfirm?.({ ...promptInfo, taskId }, filterValue, {
          isSave,
          saveParams: { ...gameInfo, task_id: taskId, name: aiSaveTitle },
          createShortIdParams: {
            ...oriValue,
            ...filterValue,
            promptInfo: { ...promptInfo, taskId },
          },
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const [tipsMap, disabled] = useMemo(() => {
    const hasRemainder = remainder > 0 || remainder === -1;
    const disabled = !hasRemainder || advancedLimitLoading;
    const tipsMap = aigcAdvancedSummaryTips(total);

    return [tipsMap, disabled];
  }, [advancedLimitLoading, total]);

  const onSelectAdvancedOptions = () => {
    setActivePromptId('');
    setErrorText('');
  };

  // 弹窗内点击「确定」时，将草稿同步回外部状态
  const handlePromptEditConfirm = (draft: PromptEditDraft) => {
    setPrompt(draft.prompt);
    setGptOptions(draft.gptOptions);
    setActivePromptId(draft.activePromptId);
    setErrorText(draft.errorText);
  };

  return (
    <>
      <FilterFixed
        filter={filterOptions}
        scrollBody={scrollBody}
        keyOperateShowOptions={[]}
        operateShowOptions={['history', 'more']}
        renderExcludeFilter={renderExcludeFilterMemo}
        hideTop={loading ? 150 : 200}
        backCondOriginData={(data) => {
          backCondOriginData(data);
        }}
        beforeCacheDealwithData={() => false}
        confirmRef={confirmRef}
        onChange={(value: any) => filterOnChange(value)}
        confirm={(value: any, oriValue: any) => {
          const newOriValue = {
            ...oriValue,
            PAGE_NAME: filterCommonParams.pageName,
          };
          oriFilter.current = newOriValue;
          // 所有的提交操作还是这个方法，下面的按钮只是触发提交
          const filter = { ...value, ...gameInfo };
          changeFilter(filter);
          if (isGenerate.current) {
            handleGptConfirm(filter, newOriValue);
            isGenerate.current = false;
          }
        }}
        finallyDealwithDefaultValue={(value, otherPageParams) => {
          if (otherPageParams?.gptOptions) {
            setPromptValue({
              prompt: otherPageParams?.prompt,
              gptOptions: otherPageParams?.gptOptions,
              saveName: otherPageParams?.aiSaveTitle,
            });
          }
          return value;
        }}
        {...filterCommonParams}
      ></FilterFixed>

      <CardCommon
        loading={loading}
        loadingHeight={240}
        className="dbo-ai-summary-setting"
        title={
          <BubbleCommon tipsMap={aigcGptModalTypeTips} placement="top">
            <img src={AiImg} className="dbo-inlineflex-w20-img" />
            {formatMessage({ id: 'AI Summary Setting' })}
          </BubbleCommon>
        }
      >
        {/* Prompt Section */}
        <div className="dbo-prompt-edit-trigger">
          <PromptSection
            prompt={prompt}
            setPrompt={setPrompt}
            gptOptions={gptOptions}
            errorText={errorText}
            setErrorText={setErrorText}
            realPromptList={realPromptList}
            defaultPromptList={defaultPromptList}
            userPromptList={savedPromptList}
            togglePromptModal={togglePromptModal}
            onSavePrompt={savePrompt}
            onUpdatePrompt={updatePrompt}
            onDeletePrompt={deletePrompt}
            onApplyPrompt={applyPrompt}
            activePromptId={activePromptId}
            setActivePromptId={setActivePromptId}
            promptEdit={promptEdit}
            fullClick={() => setPromptEditVisible(true)}
          />
        </div>
        <Form className="dbo-aigc-radio">
          {/* Summary Mode Section */}
          <Section title={formatMessage({ id: 'Summary Mode' })}>
            <div className="dbo-summary-mode-wrapper">
              <Radio.Group
                value={gptOptions}
                onChange={(value: any) => {
                  if (
                    value === 'advanced' &&
                    advancedLimitLoading &&
                    remainder === 0
                  ) {
                    return;
                  }
                  const defaultPromptItem = getDefaultPromptMap(realPromptList);
                  const defaultPrompt = getPromptText(defaultPromptItem);
                  if (value === 'basic' && gptOptions === 'advanced') {
                    if (promptEdit.current === false) {
                      setPrompt(defaultPrompt);
                      setErrorText('');
                      setActivePromptId(defaultPromptItem?.name?.['en-US'] || '');
                    } else {
                      if (!prompt.trim()) {
                        setErrorText(
                          formatMessage({ id: 'Please enter prompt.' }),
                        );
                      }
                    }
                  } else if (value === 'advanced' && gptOptions === 'basic') {
                    if (promptEdit.current === false) {
                      setPrompt('');
                    }
                    onSelectAdvancedOptions();
                  }
                  setGptOptions(value);
                }}
                className="dbo-summary-mode-group"
              >
                <div className="dbo-summary-mode-col">
                  <Radio
                    name="advanced"
                    className={`dbo-summary-mode-pill ${gptOptions === 'advanced'
                      ? 'dbo-summary-mode-pill--active'
                      : ''}`}
                    disabled={disabled}
                  >
                    <BubbleCommon
                      showIcon={false}
                      placement="top"
                      tipsMap={tipsMap}
                    >
                      <span>
                        {formatMessage({ id: 'Advanced Quantitative Summary' })}
                      </span>
                      {remainder !== -1 && (
                        <span className="dbo-summary-mode-remainder">
                          {formatMessage(
                            {
                              id: '(Remaining system uses today: {remainder}/{total})',
                            },
                            {
                              remainder: (
                                <i
                                  style={{
                                    color: remainder > 0 ? '#29B569' : '#EF5858',
                                  }}
                                >
                                  {remainder}
                                </i>
                              ),
                              total,
                            },
                          )}
                        </span>
                      )}
                    </BubbleCommon>
                  </Radio>
                  {gptOptions === 'advanced' && (
                    <div className="dbo-summary-mode-desc">
                      {formatMessage({
                        id: 'Advanced quantitative — AI captures more opinions and quantifies each precisely.'
                      })}
                    </div>
                  )}
                </div>
                <div className="dbo-summary-mode-col">
                  <Radio
                    name="basic"
                    className={`dbo-summary-mode-pill ${gptOptions === 'basic' ? 'dbo-summary-mode-pill--active' : ''}`}
                  >
                    <span>{formatMessage({ id: 'Quick' })}</span>
                    <span className="dbo-summary-mode-time">
                      {formatMessage({ id: '(<1 min)' })}
                    </span>
                  </Radio>
                  {gptOptions === 'basic' && (
                    <div className="dbo-summary-mode-desc">
                      {formatMessage({
                        id: 'Quick — faster, with fewer opinions and rougher numbers.',
                      })}
                    </div>
                  )}
                </div>
              </Radio.Group>
            </div>
          </Section>

          {/* Summary Name Section */}
          <Form.Item
            required
            showStatusIcon={false}
            label={formatMessage({ id: 'Summary Name' })}
          >
            <div className="dbo-summary-name-input-wrapper">
              <input
                className="dbo-summary-name-input"
                value={aiSaveTitle}
                onChange={(e) => setAiSaveTitle(e.target.value)}
                placeholder={formatMessage({
                  id: 'Please enter a name for report',
                })}
              />
              {aiSaveTitle && (
                <SvgIcon
                  type="clear-icon"
                  className="dbo-summary-name-clear-icon"
                  onClick={() => setAiSaveTitle('')}
                />
              )}
            </div>
          </Form.Item>
        </Form>

        {/* Footer with buttons + auto-saves info */}
        <div className="dbo-ai-summary-setting-footer">
          <div className="dbo-ai-summary-setting-footer-left">
            <Button
              type="primary"
              onClick={() => {
                isGenerate.current = true;
                filterConfirm(true);
                buriedBtnPoint(
                  'GenerateAnalysis',
                  gptType,
                  gptOptions,
                  resultType,
                );
              }}
              loading={confirmLoading}
              disabled={confirmDisabled}
            >
              {formatMessage({ id: 'Generate Analysis' })}
            </Button>
            {!!!Generate_Repeat_Report && (
              <Button
                type="weak"
                onClick={() => {
                  filterConfirm(true);
                  togglePromptModal('repeat');
                  buriedBtnPoint('GenerateRepeat');
                }}
              >
                {formatMessage({ id: 'Generate and Repeat' })}
              </Button>
            )}
            <Button
              type="weak"
              onClick={() => {
                filterReset();
                setGptOptions('advanced');
                setPrompt('');
                onSelectAdvancedOptions();
              }}
            >
              {formatMessage({ id: 'Reset' })}
            </Button>
            <Button type="weak" onClick={() => filterSave()}>
              {formatMessage({ id: 'Save Filter Setting' })}
            </Button>
          </div>
          <div className="dbo-ai-summary-setting-footer-right">
            <span className="dbo-ai-summary-setting-footer-autosave">
              {formatMessage({ id: 'Auto-saves to Summary List' })}
            </span>
            <div
              className="dbo-ai-summary-setting-footer-viewlist"
              onClick={() => changeTab?.()}
            >
              {formatMessage({ id: 'View list' })}
              <SvgIcon
                type="agent-right-arrow"
                className="dbo-ai-summary-setting-footer-viewlist-arrow"
              />
            </div>
          </div>
        </div>

        <PromptModal
          formatMessage={formatMessage}
          visible={modalVisible}
          toggleVisible={togglePromptModal}
          setPrompt={setPrompt}
          userPromptList={userPromptList}
          loading={historyLoading}
          getUserHistoryPrompt={getUserHistoryPrompt}
          modalType={modalType}
          onConfirm={() => {
            // console.log('onConfirm called');
          }}
          filter={filter}
          userInfo={userInfo}
          gameInfo={gameInfo}
          oriFilter={oriFilter.current}
          promptInfo={{
            prompt,
            task_type: gptOptions,
            model_type: gptType,
          }}
          changeTab={changeTab}
        ></PromptModal>
      </CardCommon >

      {/* 弹窗内编辑 Prompt 设置：确定时同步回外部，取消时不同步 */}
      <PromptEditModal
        visible={promptEditVisible}
        title={formatMessage({ id: 'Prompt Setting' })}
        value={{
          prompt,
          gptOptions,
          activePromptId,
          errorText,
        }}
        onConfirm={handlePromptEditConfirm}
        onClose={() => setPromptEditVisible(false)}
        realPromptList={realPromptList}
        defaultPromptList={defaultPromptList}
        userPromptList={savedPromptList}
        onSavePrompt={savePrompt}
        onUpdatePrompt={updatePrompt}
        onDeletePrompt={deletePrompt}
        onApplyPrompt={applyPrompt}
        getUserHistoryPrompt={getUserHistoryPrompt}
        historyLoading={historyLoading}
      />

      {!confirmLoading && children}
    </>
  );
}

const Section = ({
  title,
  titleTips,
  children,
}: {
  title: string;
  titleTips?: TipsMapType;
  children: React.ReactNode;
}) => {
  const { formatMessage } = useLanguage();

  return (
    <Form.Item
      label={
        <div className="item-label">
          {formatMessage({ id: title })}
          {titleTips && (
            <span style={{ verticalAlign: '-2px' }}>
              <BubbleCommon tipsMap={titleTips} placement="top"></BubbleCommon>
            </span>
          )}
        </div>
      }
    >
      {children}
    </Form.Item>
  );
};

const PromptModal = (props: any) => {
  const {
    formatMessage,
    visible,
    toggleVisible,
    setPrompt,
    userPromptList,
    loading,
    getUserHistoryPrompt,
    modalType,
    onConfirm,
    filter,
    gameInfo,
    oriFilter,
    promptInfo,
    userInfo,
    changeTab,
  } = props;
  const getCaptions = useMemo(() => {
    if (modalType === 'repeat')
      return formatMessage({ id: 'Generate and Repeat Settings' });
    if (modalType === 'save')
      return formatMessage({ id: 'Save to Analysis List' });
    if (modalType === 'history') {
      return (
        <div className="dbo-ai-summary-setting-modal-title">
          {formatMessage({ id: 'Historical prompt' })}
          <span className="dbo-ai-summary-setting-modal-tips">
            {formatMessage({ id: 'Click to apply' })}
          </span>
        </div>
      );
    }
    return null;
  }, [formatMessage, modalType]);

  const modalBody = useMemo(() => {
    if (modalType === 'history') {
      return (
        <GPT.HistoricalPrompt
          setPrompt={setPrompt}
          userPromptList={userPromptList}
          loading={loading}
          closeHistoricalPrompt={toggleVisible}
          getUserHistoryPrompt={getUserHistoryPrompt}
        ></GPT.HistoricalPrompt>
      );
    }

    if (modalType === 'repeat') {
      return (
        <RepeatSetting
          filter={filter}
          userInfo={userInfo}
          gameInfo={gameInfo}
          toggleVisible={toggleVisible}
          oriFilter={oriFilter}
          promptInfo={promptInfo}
          request={() => {
            changeTab?.(true);
          }}
        />
      );
    }

    return null;
  }, [
    modalType,
    setPrompt,
    userPromptList,
    loading,
    toggleVisible,
    getUserHistoryPrompt,
  ]);

  return (
    <Modal
      visible={visible}
      caption={getCaptions}
      onClose={toggleVisible}
      size={800}
      destroyOnClose
      className={
        modalType === 'history'
          ? 'dbo-historical-modal'
          : 'dbo-ai-summary-setting-modal'
      }
    >
      <Modal.Body>{modalBody}</Modal.Body>
    </Modal>
  );
};
